import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { Button, Result } from "antd";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isChunkError: boolean;
}

/**
 * Error Boundary component to catch chunk loading errors and other React errors
 * Automatically reloads the page when chunk loading errors are detected
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
    };
  }

  static isChunkLoadError(error: Error): boolean {
    const message = error.message.toLowerCase();
    return (
      message.includes("failed to fetch dynamically imported module") ||
      message.includes("loading chunk") ||
      message.includes("loading css chunk") ||
      message.includes("chunkloaderror") ||
      message.includes("importing a module script failed") ||
      message.includes("error loading dynamically imported module") ||
      error.name === "ChunkLoadError"
    );
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      isChunkError: ErrorBoundary.isChunkLoadError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console for debugging
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    if (ErrorBoundary.isChunkLoadError(error)) {
      console.warn("Chunk loading error detected. Reloading page...");

      // Clear caches before reload
      if ("caches" in window) {
        caches.keys().then((names) => {
          names.forEach((name) => caches.delete(name));
        });
      }

      // Force full reload with cache busting for index.html
      setTimeout(() => {
        const separator = window.location.search ? "&" : "?";
        window.location.href =
          window.location.origin +
          window.location.pathname +
          window.location.search +
          separator +
          "_refresh=" +
          Date.now();
      }, 1500);
    }
  }

  handleReload = () => {
    // Clear caches
    if ("caches" in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    // Clear localStorage if needed
    try {
      localStorage.clear();
    } catch (e) {
      console.warn("Failed to clear localStorage", e);
    }

    // Reload page
    window.location.reload();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
    });
  };

  render() {
    if (this.state.hasError) {
      // If custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Show chunk error message
      if (this.state.isChunkError) {
        return (
          <Result
            status="warning"
            title="Обновление приложения"
            subTitle="Обнаружена новая версия приложения. Страница будет перезагружена автоматически..."
            extra={[
              <Button type="primary" key="reload" onClick={this.handleReload}>
                Перезагрузить сейчас
              </Button>,
            ]}
          />
        );
      }

      // Show general error message
      return (
        <Result
          status="error"
          title="Произошла ошибка"
          subTitle="Что-то пошло не так. Пожалуйста, попробуйте перезагрузить страницу."
          extra={[
            <Button type="primary" key="reload" onClick={this.handleReload}>
              Перезагрузить страницу
            </Button>,
            <Button key="reset" onClick={this.handleReset}>
              Попробовать снова
            </Button>,
          ]}
        >
          {process.env.NODE_ENV === "development" && this.state.error && (
            <div style={{ marginTop: 24, textAlign: "left" }}>
              <details style={{ whiteSpace: "pre-wrap" }}>
                <summary style={{ cursor: "pointer", marginBottom: 8 }}>
                  <strong>Детали ошибки (только в режиме разработки)</strong>
                </summary>
                <pre
                  style={{
                    background: "#f5f5f5",
                    padding: 16,
                    borderRadius: 4,
                    overflow: "auto",
                    maxHeight: 400,
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            </div>
          )}
        </Result>
      );
    }

    return this.props.children;
  }
}
